import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Minus,
  Plus,
  ChevronDown,
  Search,
  Calendar,
  Clock,
  Percent,
  Bell,
  Mail,
  Link2,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import DateCalendar from "./DateCalendar";
import TimeSpinner from "./ui/TimeSpinner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { format, parseISO, isValid, parse } from "date-fns";
import { cn } from "../lib/utils";
import GlobalLoader from "./ui/GlobalLoader";
import { Booking, BookingStatus } from "../services/BookingsApi";
import { useAuth } from "../auth/AuthContext";
import { getBusinessServices } from "../services/businessServicesApi";
import { getAdminProducts, getProductTaxes } from "../services/productsApi";
import { BusinessService } from "../types/businessService";
import { Product } from "../types/product";
import { ChevronRight, FileText } from "lucide-react";
import { Tax } from "../types/tax";
import AssignConsultationFormModal from "./modals/AssignConsultationFormModal";
import {
  createDepositPaymentLink,
  sendBookingConfirmation,
  sendBookingReminder,
} from "../services/bookings";
import { useToast } from "../hooks/useToast";
import DepositLinkPopup from "./modals/DepositLinkPopup";

interface EmployeeOption {
  id: number;
  name: string;
}

interface LineItem {
  id: string;
  name: string;
  unitPrice: number;
  quantity: number;
  type: string;
  serviceId?: string;
  productId?: string;
  taxPercent: number;
  discount: number;
  discountType: "percent" | "fixed" | "amount";
  originalPrice?: number;
}

interface BookingEditDrawerProps {
  isOpen: boolean;
  booking: Booking | null;
  loading: boolean;
  employees: EmployeeOption[];
  onClose: () => void;
  onSubmit: (payload: {
    bookingId: number;
    bookingDate: string;
    bookingTime: string;
    status: BookingStatus;
    employeeId?: number;
    lineItems: LineItem[];
  }) => void;
  onDelete: (bookingId: number) => void;
}

const statusOptions: BookingStatus[] = [
  "Booked",
  "Pending",
  "Completed",
  "In Progress",
  "No Show",
  "Cancelled",
];

const BookingEditDrawer: React.FC<BookingEditDrawerProps> = ({
  isOpen,
  booking,
  loading,
  employees,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [status, setStatus] = useState<BookingStatus>("Booked");
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  // Services and Products state
  const [services, setServices] = useState<BusinessService[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [allTaxes, setAllTaxes] = useState<Tax[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");

  const [showAssignFormModal, setShowAssignFormModal] = useState(false);
  const [actionBusy, setActionBusy] = useState<
    "confirm" | "reminder" | "deposit" | null
  >(null);
  const [depositLink, setDepositLink] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState<number | null>(null);
  const [depositCurrency, setDepositCurrency] = useState<string | null>(null);
  const [showDepositPopup, setShowDepositPopup] = useState(false);
  const toast = useToast();

  const paymentStatus = String(
    (booking?.rawData as any)?.payment_status ?? ""
  ).toLowerCase();
  // Backend recomputes deposit from services when bookings.deposit is empty
  const canRequestDeposit =
    !!booking &&
    paymentStatus !== "completed" &&
    Number(booking.pending ?? (booking?.rawData as any)?.total_pending ?? 1) >
      0 &&
    booking.status !== "Completed" &&
    booking.status !== "Cancelled";

  const handleSendConfirmation = async () => {
    if (!booking?.id || actionBusy) return;
    setActionBusy("confirm");
    try {
      const res = await sendBookingConfirmation(booking.id);
      toast.success(res?.message || "Confirmation sent.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to send confirmation.");
    } finally {
      setActionBusy(null);
    }
  };

  const handleSendReminder = async () => {
    if (!booking?.id || actionBusy) return;
    setActionBusy("reminder");
    try {
      const res = await sendBookingReminder(booking.id);
      toast.success(res?.message || "Reminder sent.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to send reminder.");
    } finally {
      setActionBusy(null);
    }
  };

  const handleRequestDeposit = async () => {
    if (!booking?.id || actionBusy) return;
    setActionBusy("deposit");
    try {
      const res = await createDepositPaymentLink(booking.id, true);
      const url = res?.short_url || res?.payment_url || null;
      setDepositLink(url);
      setDepositAmount(res?.amount != null ? Number(res.amount) : null);
      setDepositCurrency(res?.currency || null);
      setShowDepositPopup(!!url);
      toast.success(res?.message || "Deposit payment link sent.");
      if (url && navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          /* ignore clipboard errors */
        }
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to create deposit payment link.");
    } finally {
      setActionBusy(null);
    }
  };

  // Fetch services and products when drawer opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchServices = async () => {
      setServicesLoading(true);
      try {
        const data = await getBusinessServices();
        setServices(data.filter((s) => s.status === "active"));
      } catch (error) {
        console.error("Failed to fetch services", error);
      } finally {
        setServicesLoading(false);
      }
    };

    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const data = await getAdminProducts();
        setProducts(data);
      } catch (error) {
        console.error("Failed to fetch products", error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    const fetchTaxes = async () => {
      try {
        const data = await getProductTaxes();
        setAllTaxes(data);
      } catch (error) {
        console.error("Failed to fetch taxes", error);
      }
    };

    fetchServices();
    fetchProducts();
    fetchTaxes();
  }, [isOpen]);

  useEffect(() => {
    if (!booking) return;

    const dateSource = booking.rawData?.date_time;
    let parsedDate = dateSource ? new Date(dateSource) : new Date(NaN);

    // Fallback to bookingTime if rawData is missing or invalid
    if (isNaN(parsedDate.getTime()) && booking.bookingTime) {
      // Try to parse the formatted time (e.g., "23 Dec 2025 10:42 PM")
      parsedDate = new Date(booking.bookingTime);
    }

    // Ultimate fallback to current date if all else fails
    if (isNaN(parsedDate.getTime())) {
      parsedDate = new Date();
    }

    setBookingDate(parsedDate.toISOString().split("T")[0]);
    setBookingTime(
      `${String(parsedDate.getHours()).padStart(2, "0")}:${String(
        parsedDate.getMinutes()
      ).padStart(2, "0")}`
    );
    setStatus(booking.status);
    setDepositLink(null);
    setDepositAmount(null);
    setDepositCurrency(null);
    setShowDepositPopup(false);

    const employeeSource =
      booking.rawData?.employee?.id ||
      booking.rawData?.employee_id ||
      booking.rawData?.staff?.id ||
      booking.rawData?.staff_id;
    setEmployeeId(
      typeof employeeSource === "number" ? employeeSource : undefined
    );

    const items = Array.isArray(booking.rawData?.items)
      ? (booking.rawData?.items as any[])
      : [];

    setLineItems(
      items.map((item, idx) => {
        const rawTax = item.tax_percent || item.tax_amount || 0; // Fallback logic
        // If tax_percent is missing but we have tax_amount, calculate percent? Hard without base.
        // Assume tax_percent usually comes from backend in items or we default to 0.
        // Better: items from POS storePayload have tax_percent.

        return {
          id: String(item.id ?? `line-${idx}`),
          name: item.name || `Line Item ${idx + 1}`,
          unitPrice: Number(item.unit_price || item.amount || 0),
          quantity: Number(item.qty || item.quantity || 1),
          type: item.type || (item.product_id ? "product" : "service"),
          serviceId: item.business_service_id ? String(item.business_service_id) : undefined,
          productId: item.product_id ? String(item.product_id) : undefined,
          taxPercent: Number(rawTax),
          discount: Number(item.discount || 0),
          discountType: item.discount_type || "fixed",
          originalPrice: undefined, // Could try to infer if we assume unitPrice is discounted
        };
      })
    );
  }, [booking]);

  const totals = useMemo(() => {
    let serviceTotal = 0; // Gross
    let productTotal = 0; // Gross
    let taxTotal = 0;
    let discountTotal = 0;

    lineItems.forEach((item) => {
      // Net amount (what is currently stored in unitPrice)
      const currentUnitPrice = item.unitPrice;
      const quantity = item.quantity;
      const netLineAmount = currentUnitPrice * quantity;

      // Gross amount
      // If originalPrice is set, use it. Otherwise, use unitPrice as the gross.
      const originalUnitPrice = item.originalPrice !== undefined ? item.originalPrice : item.unitPrice;
      const grossLineAmount = originalUnitPrice * quantity;

      const lineDiscount = grossLineAmount - netLineAmount;
      const lineTax = (netLineAmount * item.taxPercent) / 100;

      taxTotal += lineTax;
      discountTotal += lineDiscount;

      if (item.type === "service") {
        serviceTotal += grossLineAmount;
      } else {
        productTotal += grossLineAmount;
      }
    });

    return {
      serviceTotal,
      productTotal,
      taxTotal,
      discountTotal,
      total: (serviceTotal + productTotal) - discountTotal + taxTotal,
    };
  }, [lineItems]);

  // Backend booking-detail endpoints may return payment aggregates at the top level.
  // We prefer those to keep due/paid consistent with the API response.
  const backendPaidAmount =
    Number(
      (booking?.rawData as any)?.total_paid ??
        (booking?.rawData as any)?.amountPaid ??
        booking?.paid ??
        0,
    ) || 0;

  const backendDueAmount =
    Number(
      (booking?.rawData as any)?.total_pending ??
        (booking?.rawData as any)?.pending_amount ??
        booking?.pending ??
        0,
    ) || 0;

  const updateQuantity = (lineId: string, delta: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineId
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    );
  };

  const removeLineItem = (lineId: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== lineId));
  };

  const addService = (service: BusinessService) => {
    // Calculate Tax Percent for this service
    // service.taxes is array of tax IDs (strings). Map to allTaxes to sum percentages.
    const serviceTaxIds = (service.taxes || []).map(id => Number(id));
    const totalTaxPercent = allTaxes
      .filter((t) => serviceTaxIds.includes(t.id))
      .reduce((sum, t) => sum + t.percent, 0);

    const price = service.discountPrice || service.price;
    const isDiscounted = service.discount && service.discount > 0;

    const newItem: LineItem = {
      id: `service-${service.id}-${Date.now()}`,
      name: service.name,
      unitPrice: price,
      quantity: 1,
      type: "service",
      serviceId: service.id,
      taxPercent: totalTaxPercent,
      discount: service.discount || 0,
      discountType: service.discountType === "amount" ? "fixed" : "percent",
      originalPrice: isDiscounted ? service.price : undefined,
    };
    setLineItems((prev) => [...prev, newItem]);
    setShowServiceDropdown(false);
    setServiceSearchTerm("");
  };

  const addProduct = (product: Product) => {
    // Product taxes are objects in product.taxes
    const totalTaxPercent = product.taxes?.reduce((sum, t) => sum + t.percent, 0) || 0;

    const price = product.discountedPrice || product.price;
    const isDiscounted = product.discountedPrice !== undefined && product.discountedPrice < product.price;

    const newItem: LineItem = {
      id: `product-${product.id}-${Date.now()}`,
      name: product.name,
      unitPrice: price,
      quantity: 1,
      type: "product",
      productId: product.id,
      taxPercent: totalTaxPercent,
      discount: product.discount || 0,
      discountType: product.discountType || "percent",
      originalPrice: isDiscounted ? product.price : undefined,
    };
    setLineItems((prev) => [...prev, newItem]);
    setShowProductDropdown(false);
    setProductSearchTerm("");
  };

  const filteredServices = useMemo(() => {
    if (!serviceSearchTerm) return services;
    const term = serviceSearchTerm.toLowerCase();
    return services.filter((s) => s.name.toLowerCase().includes(term));
  }, [services, serviceSearchTerm]);

  const filteredProducts = useMemo(() => {
    if (!productSearchTerm) return products;
    const term = productSearchTerm.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(term));
  }, [products, productSearchTerm]);

  const handleSubmit = () => {
    if (!booking) return;
    onSubmit({
      bookingId: booking.id,
      bookingDate,
      bookingTime,
      status,
      employeeId,
      lineItems,
    });
  };

  const { user } = useAuth();
  const authCurrency =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "";

  const currency =
    booking?.rawData?.company?.currency?.currency_symbol ||
    booking?.rawData?.company?.currency?.currency_code ||
    authCurrency;


  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[999]"
          onClick={onClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-5xl bg-white shadow-2xl z-[1000] transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        {isOpen && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
          >
            <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
          </motion.button>
        )}
        <div className="h-full flex flex-col bg-[#F9F9F9]">
          {/* Header */}
          <div className="bg-white border-b border-gray-100 shadow-sm z-10 sticky top-0">
            <div className="px-4 sm:px-8 pt-10 pb-6 flex items-end justify-between">
              <div>
                <p className="text-[10px] sm:text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">
                  Manage Appointment
                </p>
                <h2 className="text-xl sm:text-[27px] font-[600] text-[#402B69] leading-tight">
                  Edit Booking
                </h2>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 mb-1">
                <button
                  type="button"
                  onClick={handleSendConfirmation}
                  disabled={!!actionBusy || !booking}
                  className="flex items-center gap-2 px-3 py-2 bg-[#885ABB]/10 text-[#885ABB] rounded-xl text-xs font-bold hover:bg-[#885ABB] hover:text-white transition-all disabled:opacity-50"
                >
                  {actionBusy === "confirm" ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Mail size={14} />
                  )}
                  <span>Confirm</span>
                </button>
                <button
                  type="button"
                  onClick={handleSendReminder}
                  disabled={!!actionBusy || !booking}
                  className="flex items-center gap-2 px-3 py-2 bg-[#885ABB]/10 text-[#885ABB] rounded-xl text-xs font-bold hover:bg-[#885ABB] hover:text-white transition-all disabled:opacity-50"
                >
                  {actionBusy === "reminder" ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Bell size={14} />
                  )}
                  <span>Reminder</span>
                </button>
                {canRequestDeposit && (
                  <button
                    type="button"
                    onClick={handleRequestDeposit}
                    disabled={!!actionBusy}
                    className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-xs font-bold hover:bg-emerald-600 hover:text-white transition-all disabled:opacity-50"
                  >
                    {actionBusy === "deposit" ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Link2 size={14} />
                    )}
                    <span>Request Deposit</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowAssignFormModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#885ABB]/10 text-[#885ABB] rounded-xl text-xs font-bold hover:bg-[#885ABB] hover:text-white transition-all"
                >
                  <FileText size={16} />
                  <span>Send Forms</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto xl:overflow-hidden px-4 sm:px-8 py-6 custom-scrollbar">
            {loading && (
              <div className="flex h-64 items-center justify-center">
                <GlobalLoader
                  initialPageLoad={false}
                  compensateSidebar={false}
                  description="Loading booking details..."
                />
              </div>
            )}

            {!loading && booking && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 h-auto xl:h-full">
                <div className="space-y-6 overflow-visible xl:overflow-y-auto custom-scrollbar pr-0 xl:pr-2 pb-6">
                  {/* Customer Information Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
                    <div className="flex flex-col items-center text-center space-y-4 mb-10">
                      <div className="w-24 h-24 rounded-full bg-[#885ABB]/10 flex items-center justify-center text-[#885ABB] text-3xl font-bold shadow-sm border-4 border-white">
                        {booking.customerName.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-xl font-[600] text-[#402B69] mb-1">
                          {booking.customerName}
                        </h3>
                        <p className="text-sm text-gray-400 font-medium">
                          {booking.rawData?.customer?.email || "No email provided"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                            Booking Date
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "h-12 w-full border border-gray-200 bg-white rounded-xl text-sm font-semibold text-[#402B69] px-4 shadow-sm hover:border-[#885ABB] transition-all justify-between",
                                  !bookingDate && "text-muted-foreground"
                                )}
                              >
                                {bookingDate && isValid(parseISO(bookingDate))
                                  ? format(parseISO(bookingDate), "dd MMM yyyy")
                                  : "Select date"}
                                <Calendar className="h-4 w-4 text-[#885ABB]" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <DateCalendar
                                selectedDate={
                                  bookingDate && isValid(parseISO(bookingDate))
                                    ? parseISO(bookingDate)
                                    : new Date()
                                }
                                onSelectDate={(date) => {
                                  if (date) {
                                    setBookingDate(format(date, "yyyy-MM-dd"));
                                  }
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                            Booking Time
                          </label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "h-12 w-full border border-gray-200 bg-white rounded-xl text-sm font-semibold text-[#402B69] px-4 shadow-sm hover:border-[#885ABB] transition-all justify-between",
                                  !bookingTime && "text-muted-foreground"
                                )}
                              >
                                {bookingTime
                                  ? format(
                                    parse(bookingTime, "HH:mm", new Date()),
                                    "h:mm aa"
                                  )
                                  : "Select time"}
                                <Clock className="h-4 w-4 text-[#885ABB]" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-4 bg-white border border-gray-200 shadow-xl rounded-xl z-[9999]" align="start">
                              <TimeSpinner
                                date={
                                  bookingTime
                                    ? parse(bookingTime, "HH:mm", new Date())
                                    : new Date()
                                }
                                onChange={(time: string) => setBookingTime(time)}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-1">
                            Booking Status
                          </label>
                          {booking.status === "Completed" ? (
                            <div className="flex items-center justify-between h-12 px-4 rounded-xl bg-green-50 text-sm text-green-700 font-bold border border-green-100">
                              <span className="capitalize">{status}</span>
                              <span className="text-[10px] uppercase opacity-60">Final</span>
                            </div>
                          ) : (
                            <Select
                              value={status}
                              onValueChange={(value) =>
                                setStatus(value as BookingStatus)
                              }
                            >
                              <SelectTrigger className="h-12 border border-gray-200 bg-white rounded-xl text-sm font-semibold text-[#402B69] shadow-sm hover:border-[#885ABB] transition-all">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 z-[9999]">
                                {statusOptions.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        {/* Staff dropdown intentionally hidden (staff is set from existing booking data). */}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col h-auto xl:h-full overflow-visible xl:overflow-hidden space-y-6">
                  {/* Line Items Card */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden flex-1">
                    <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
                      <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">
                        Services & Products
                      </h3>
                      <div className="flex items-center gap-2">
                        {/* Popovers remain same but shrink-0 */}
                        <Popover open={showServiceDropdown} onOpenChange={setShowServiceDropdown}>
                          <PopoverTrigger asChild>
                            <button
                              onClick={() => setShowProductDropdown(false)}
                              className="h-10 px-4 text-xs font-bold text-white bg-[#885ABB] rounded-xl hover:bg-[#6f47a2] transition flex items-center justify-center gap-2 shadow-sm shrink-0"
                            >
                              <Plus size={14} strokeWidth={3} />
                              <span>Service</span>
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-0 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] overflow-hidden flex flex-col" align="end">
                            <div className="p-4 border-b border-gray-50 bg-[#F9F9F9]">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search services..."
                                  value={serviceSearchTerm}
                                  onChange={(e) => setServiceSearchTerm(e.target.value)}
                                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#885ABB] focus:ring-4 focus:ring-[#885ABB]/10 transition-all font-medium"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                              {servicesLoading ? (
                                <div className="p-8 text-center text-sm text-gray-400 font-medium">
                                  Loading services...
                                </div>
                              ) : filteredServices.length === 0 ? (
                                <div className="p-8 text-center text-sm text-gray-400 font-medium">
                                  No services found
                                </div>
                              ) : (
                                filteredServices.map((service) => (
                                  <button
                                    key={service.id}
                                    onClick={() => addService(service)}
                                    className="w-full text-left px-5 py-4 hover:bg-[#885ABB]/5 transition border-b border-gray-50 last:border-b-0 group"
                                  >
                                    <div className="font-bold text-sm text-[#402B69] group-hover:text-[#885ABB] transition-colors">
                                      {service.name}
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-bold mt-1.5 uppercase tracking-wider flex items-center gap-2">
                                      <span className="text-[#885ABB]">{currency}{service.discountPrice || service.price}</span>
                                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                      <span>{service.durationLabel}</span>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>

                        <Popover open={showProductDropdown} onOpenChange={setShowProductDropdown}>
                          <PopoverTrigger asChild>
                            <button
                              onClick={() => setShowServiceDropdown(false)}
                              className="h-10 px-4 text-xs font-bold text-[#885ABB] border border-[#885ABB] rounded-xl hover:bg-purple-50 transition flex items-center justify-center gap-2 shadow-sm bg-white shrink-0"
                            >
                              <Plus size={14} strokeWidth={3} />
                              <span>Product</span>
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-0 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] overflow-hidden flex flex-col" align="end">
                            <div className="p-4 border-b border-gray-50 bg-[#F9F9F9]">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search products..."
                                  value={productSearchTerm}
                                  onChange={(e) => setProductSearchTerm(e.target.value)}
                                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#885ABB] focus:ring-4 focus:ring-[#885ABB]/10 transition-all font-medium"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                              {productsLoading ? (
                                <div className="p-8 text-center text-sm text-gray-400 font-medium">
                                  Loading products...
                                </div>
                              ) : filteredProducts.length === 0 ? (
                                <div className="p-8 text-center text-sm text-gray-400 font-medium">
                                  No products found
                                </div>
                              ) : (
                                filteredProducts.map((product) => (
                                  <button
                                    key={product.id}
                                    onClick={() => addProduct(product)}
                                    className="w-full text-left px-5 py-4 hover:bg-[#885ABB]/5 transition border-b border-gray-50 last:border-b-0 group"
                                  >
                                    <div className="font-bold text-sm text-[#402B69] group-hover:text-[#885ABB] transition-colors">
                                      {product.name}
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-bold mt-1.5 uppercase tracking-wider flex items-center gap-2">
                                      <span className="text-[#885ABB]">{currency}{product.discountedPrice || product.price}</span>
                                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                      <span>Stock: {product.stock || 0}</span>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100 bg-white overflow-y-auto custom-scrollbar flex-1 min-h-0">
                      {lineItems.length === 0 && (
                        <div className="text-sm text-gray-400 font-medium px-6 py-12 text-center">
                          No items added to this booking yet.
                        </div>
                      )}
                      {lineItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center px-4 sm:px-6 py-4 gap-4 hover:bg-gray-50/50 transition-all border-b border-gray-50 last:border-0"
                        >
                          {/* Name & Type & Unit Price */}
                          <div className="flex-1 min-w-0 flex flex-col gap-1">
                            <div className="flex items-start gap-2">
                              <span
                                className="text-[14px] font-bold text-[#402B69] leading-tight"
                                title={item.name}
                              >
                                {item.name || "Unnamed Item"}
                              </span>
                              <span className={cn(
                                "shrink-0 text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider border",
                                item.type === "service"
                                  ? "bg-purple-50 text-[#885ABB] border-purple-100"
                                  : "bg-blue-50 text-blue-600 border-blue-100"
                              )}>
                                {item.type}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                              {item.originalPrice && item.originalPrice > item.unitPrice && (
                                <span className="line-through decoration-red-400 text-gray-300">
                                  {currency}{item.originalPrice.toFixed(2)}
                                </span>
                              )}
                              <span>
                                {currency}{item.unitPrice.toFixed(2)}
                              </span>
                              {item.taxPercent > 0 && (
                                <span className="text-[10px] text-gray-400">
                                  (+ Taxes)
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Controls & Total */}
                          <div className="flex items-center gap-4 sm:gap-6 shrink-0">
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-1 border border-gray-100 shadow-sm">
                              <button
                                type="button"
                                className="w-8 h-8 rounded-lg border border-gray-200 text-gray-400 flex items-center justify-center hover:border-[#885ABB] hover:text-[#885ABB] hover:bg-white transition-all bg-white"
                                onClick={() => updateQuantity(item.id, -1)}
                              >
                                <Minus size={14} />
                              </button>
                              <span className="w-8 text-center text-[14px] font-bold text-[#402B69]">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                className="w-8 h-8 rounded-lg border border-gray-200 text-gray-400 flex items-center justify-center hover:border-[#885ABB] hover:text-[#885ABB] hover:bg-white transition-all bg-white"
                                onClick={() => updateQuantity(item.id, 1)}
                              >
                                <Plus size={14} />
                              </button>
                            </div>

                            {/* Total for this line */}
                            <div className="w-24 text-right">
                              <span className="text-[15px] font-extrabold text-[#402B69]">
                                {currency}{(item.unitPrice * item.quantity).toFixed(2)}
                              </span>
                            </div>

                            {/* Remove Button */}
                            <button
                              onClick={() => removeLineItem(item.id)}
                              className="w-9 h-9 rounded-full border border-red-50 text-red-400 flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition-all group shrink-0"
                              title="Remove item"
                            >
                              <X size={16} className="group-hover:rotate-90 transition-transform" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm shrink-0">
                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1 mb-6">
                      Payment Summary
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Service Total</span>
                        <span className="font-semibold text-gray-900">
                          {currency}
                          {totals.serviceTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Product Total</span>
                        <span className="font-semibold text-gray-900">
                          {currency}
                          {totals.productTotal.toFixed(2)}
                        </span>
                      </div>
                      {totals.discountTotal > 0 && (
                        <div className="flex items-center justify-between text-sm text-green-600">
                          <span>Total Discount</span>
                          <span className="font-bold">
                            -{currency}
                            {totals.discountTotal.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Taxes</span>
                        <span className="font-semibold text-gray-900">
                          {currency}
                          {totals.taxTotal.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Paid Amount</span>
                        <span className="font-semibold text-gray-900">
                          {currency}
                          {backendPaidAmount.toFixed(2)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-base border-t border-gray-50 pt-5 mt-2">
                        <span className="font-semibold text-[#402B69]">
                          {booking.status === "Completed" ? "Total Paid" : "Total Amount"}
                        </span>
                        <span className="text-2xl font-bold text-[#885ABB]">
                          {currency}
                          {booking.status === "Completed"
                            ? backendPaidAmount.toFixed(2)
                            : backendDueAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!loading && booking && (
            <div className="px-4 sm:px-8 pb-8 pt-4 bg-white border-t border-gray-100 z-20 flex justify-end">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => onDelete(booking.id)}
                  className="px-4 h-10 bg-white border border-red-100 text-[#BB5A5E] rounded-lg font-bold hover:bg-red-50 transition-all text-xs uppercase tracking-wider"
                >
                  Delete
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-12 h-10 bg-[#885ABB] text-white rounded-lg font-bold hover:bg-[#7748A4] shadow-md shadow-[#885ABB]/10 transition-all flex items-center justify-center gap-2 group text-xs uppercase tracking-wider min-w-[180px]"
                >
                  <span>Save Changes</span>
                  <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div >
      <AssignConsultationFormModal
        isOpen={showAssignFormModal}
        onClose={() => setShowAssignFormModal(false)}
        bookingId={booking?.id || 0}
        customerId={Number(booking?.rawData?.customer?.id || booking?.rawData?.user?.id || 0)}
      />
      <DepositLinkPopup
        isOpen={showDepositPopup}
        link={depositLink}
        amount={depositAmount}
        currency={depositCurrency}
        onClose={() => setShowDepositPopup(false)}
      />
    </>
  );
};

export default BookingEditDrawer;
